import { joinURL } from 'ufo'
import { api } from '~/api/apis'
import {
  httpErrorSchema,
  type HttpErrorType
} from '~/api/helper/api-error-handler'
import type { ApiResponse } from '~/api/helper/api-response-schema'
import { apiInstance } from '~/api/instance'
import { isType } from '~/utils/functions/is-type'

type Action = 'read' | 'create' | 'update' | 'delete'

export function useAdminApi<T extends ApiResponse<unknown>>(
  requestApi: Promise<T>
) {
  const commonStore = useCommonStore()

  const { formatUrl } = useUrl()

  async function call(options: {
    action: Action
    ignoreStatus?: number[]
  }): Promise<
    | {
        success: true
        response: T
      }
    | {
        success: false
        response: ApiResponse<null>
      }
    | {
        success: true
        response: {
          status: 204
          message: string
          success: true
          total: null
          data: null
        }
      }
  > {
    const { action, ignoreStatus } = options

    try {
      const response = await requestApi

      if (response.success) {
        setSuccessSnack(options?.action)
        return { success: response.success, response }
      } else if (!ignoreStatus?.includes(response.status)) {
        setFailSnack({ action, status: response.status })
      }

      return {
        success: false,
        response
      } as any
    } catch (error) {
      const errorData = error as HttpErrorType
      const result = await handleError({ action, error: errorData })

      if (result?.success) {
        return {
          success: true,
          response: result as any
        }
      } else {
        return {
          success: false,
          response: result as any
        }
      }
    }
  }

  return {
    call
  }

  async function handleError(options: {
    action: Action
    error: HttpErrorType
  }) {
    const { action, error } = options
    const { baseURL } = useEnvStore()

    if (isType(error, httpErrorSchema) && error.error.status === 401) {
      try {
        await api.auth.refreshAdminToken()
        const { url, requestOptions } = error.requestInfo
        return await apiInstance<ApiResponse<any>>(
          formatUrl(url, true),
          requestOptions
        )
      } catch {
        commonStore.setSnack({ action: 'fail', message: 'Refresh Token 失效' })
        removeAuthCookies()
        window.location.href = joinURL(baseURL, '/sign')
        return undefined
      }
    } else if (isType(error, httpErrorSchema)) {
      setFailSnack({ action, status: error.error.status })
    }

    return undefined
  }

  function setSuccessSnack(action?: Action) {
    switch (action) {
      case 'create':
        commonStore.setSnack({
          action: 'success',
          message: '新增資料成功'
        })
        break
      case 'update':
        commonStore.setSnack({
          action: 'success',
          message: '更新資料成功'
        })
        break
      case 'delete':
        commonStore.setSnack({
          action: 'success',
          message: '刪除資料成功'
        })
    }
  }

  function setFailSnack(options: { action: Action; status: number }) {
    const { action, status } = options

    if (status === 204) {
      return
    }

    if (status === 522) {
      commonStore.setSnack({
        action: 'fail',
        message: '該帳號已註冊'
      })
      return
    }

    if (status === 409) {
      commonStore.setSnack({
        action: 'fail',
        message: '該產品技術支援人員已設定(僅限1人)'
      })
      return
    }

    switch (action) {
      case 'create':
        commonStore.setSnack({ action: 'fail', message: '無法新增資料' })
        break
      case 'read':
        commonStore.setSnack({ action: 'fail', message: '無法取得資料' })
        break
      case 'update':
        commonStore.setSnack({ action: 'fail', message: '無法更新資料' })
        break
      case 'delete':
        commonStore.setSnack({ action: 'fail', message: '無法刪除資料' })
        break
    }
  }
}
